/**
 * Tenant theme / branding — the contract of `PATCH /api/config/theme`.
 *
 * The body is written into the tenant's `platform_configs` row and painted
 * into every member's CSS custom properties, so it is validated whole: only
 * the known colour slots and asset slots, a colour is a hex colour (what the
 * theme editors' pickers produce), and an asset is an absolute http(s) URL.
 * A `blob:` URL, a `url(...)` expression or an unknown key is refused rather
 * than stored for every member of the tenant to load.
 */
import { z } from 'zod';

/** `#RGB`, `#RGBA`, `#RRGGBB` or `#RRGGBBAA`. */
export const hexColorSchema = z.string().regex(/^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);

/** An absolute http(s) URL — something every member's browser can fetch. */
export const themeAssetUrlSchema = z.url({ protocol: /^https?$/ }).max(2048);

export const themeColorsSchema = z.strictObject({
  // Brand
  primary: hexColorSchema.optional(),
  primaryForeground: hexColorSchema.optional(),
  secondary: hexColorSchema.optional(),
  secondaryForeground: hexColorSchema.optional(),
  accent: hexColorSchema.optional(),
  accentForeground: hexColorSchema.optional(),
  // Semantic
  success: hexColorSchema.optional(),
  warning: hexColorSchema.optional(),
  error: hexColorSchema.optional(),
  // Surface
  background: hexColorSchema.optional(),
  foreground: hexColorSchema.optional(),
  card: hexColorSchema.optional(),
  cardForeground: hexColorSchema.optional(),
  muted: hexColorSchema.optional(),
  mutedForeground: hexColorSchema.optional(),
  border: hexColorSchema.optional(),
  // Popover
  popover: hexColorSchema.optional(),
  popoverForeground: hexColorSchema.optional(),
  // Input & focus
  input: hexColorSchema.optional(),
  ring: hexColorSchema.optional(),
  // Sidebar
  sidebar: hexColorSchema.optional(),
  sidebarForeground: hexColorSchema.optional(),
  sidebarPrimary: hexColorSchema.optional(),
  sidebarPrimaryForeground: hexColorSchema.optional(),
  sidebarAccent: hexColorSchema.optional(),
  sidebarAccentForeground: hexColorSchema.optional(),
  sidebarBorder: hexColorSchema.optional(),
  sidebarRing: hexColorSchema.optional(),
});

export const themeAssetsSchema = z.strictObject({
  primaryLogo: themeAssetUrlSchema.optional(),
  secondaryLogo: themeAssetUrlSchema.optional(),
  appIcon: themeAssetUrlSchema.optional(),
  favicon: themeAssetUrlSchema.optional(),
});

/** `PATCH /api/config/theme` — a partial update the API merges into the stored theme. */
export const updateThemeConfigSchema = z.strictObject({
  colors: themeColorsSchema.optional(),
  assets: themeAssetsSchema.optional(),
  isActive: z.boolean().optional(),
});

export type ThemeColorsInput = z.infer<typeof themeColorsSchema>;
export type ThemeAssetsInput = z.infer<typeof themeAssetsSchema>;
export type UpdateThemeConfigInput = z.infer<typeof updateThemeConfigSchema>;
